import { useFillsGraphConfigurationSelector } from 'widgets/fillsGraph/store/configuration/selectors';

import { FillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';
import { FillsGraphData } from 'widgets/fillsGraph/fillsGraphData';
import { vwapLabel, xVwapLabel } from 'widgets/fillsGraph/legend/labels';
import {
    vwapTooltip,
    xvwapTooltip,
} from 'widgets/fillsGraph/orderFills/help/orderFillsLegendTooltip';
import { useSelectLocked } from 'layoutRenderer/store/selectors';
import { TooltipText } from 'utils/components/Tooltip/Tooltip';
import { useMemo } from 'react';
import { FillsGraphConfiguration } from 'widgets/fillsGraph/store/configuration/FillsGraphConfiguration';

export interface FillsGraphLegendStatus {
    allOrderFillsHidden: boolean;
    vwapLegendLabel: string;
    vwapLegendTooltip: TooltipText;
    displayAvgExecutionLegend: boolean;
    displayPercentageCompletedLegend: boolean;
    displayIndexLegend: boolean;
    displayPriceCandleLegends: boolean;
    allExpectedVsActualHidden: boolean;
    allPercentageOfMarketVolumeHidden: boolean;
    allPlChartHidden: boolean;
    allPerformanceVsIndexChartHidden: boolean;
    hideFillPriceLegend: boolean;
    hideFillVolumeLegend: boolean;
}

export function useLegendStatus(
    fillsGraphData: FillsGraphData,
    fillsGraphPortfolioData: FillsGraphPortfolioData
): FillsGraphLegendStatus {
    const config = useFillsGraphConfigurationSelector();
    const locked = useSelectLocked();

    return useMemo(
        () =>
            getLegendStatus(
                config,
                fillsGraphData,
                fillsGraphPortfolioData,
                locked
            ),
        [config, fillsGraphData, fillsGraphPortfolioData, locked]
    );
}

export function getLegendStatus(
    config: FillsGraphConfiguration,
    fillsGraphData: FillsGraphData,
    fillsGraphPortfolioData: FillsGraphPortfolioData,
    locked: boolean
) {
    const { relativePerformanceIndex } = fillsGraphData;
    const { hasFullOrderData } = fillsGraphPortfolioData;

    return {
        ...getOrderFillsLegendStatus(),
        ...getExpectedVsActualLegendStatus(),
        ...getPercentageOfMarketVolumeLegendStatus(),
        ...getPlChartLegendStatus(),
        ...getPerformanceVsIndexLegendStatus(),
    };
    function getOrderFillsLegendStatus() {
        const displayAvgExecutionLegend = hasFullOrderData;
        const displayPercentageCompletedLegend = hasFullOrderData;
        const vwapLegendLabel = hasFullOrderData ? xVwapLabel : vwapLabel;
        const vwapLegendTooltip = hasFullOrderData ? xvwapTooltip : vwapTooltip;

        const {
            OrderFills: {
                hidePriceLegend,
                hideAvgExecutionLegend,
                hideVWAPLegend,
                hidePercentageCompletedLegend,
                hideIndexLegend,
                hideFillPriceLegend,
                hideFillVolumeLegend,
                displayPriceCandles,
                hidePriceCandleLegends,
            },
        } = config;

        const displayIndexLegend = !!relativePerformanceIndex;
        const displayPriceCandleLegends =
            displayPriceCandles && !hidePriceCandleLegends;

        const allOrderFillsHidden =
            locked &&
            hidePriceLegend &&
            (hideAvgExecutionLegend || !displayAvgExecutionLegend) &&
            hideVWAPLegend &&
            (hidePercentageCompletedLegend ||
                !displayPercentageCompletedLegend) &&
            (hideIndexLegend || !displayIndexLegend);

        return {
            allOrderFillsHidden,
            vwapLegendLabel,
            vwapLegendTooltip,
            displayAvgExecutionLegend,
            displayPercentageCompletedLegend,
            displayIndexLegend,
            hideFillPriceLegend,
            hideFillVolumeLegend,
            displayPriceCandleLegends,
        };
    }

    function getExpectedVsActualLegendStatus() {
        const {
            ExpectedVsActual: { hideActualLegend, hideExpectedLegend },
        } = config;
        const allExpectedVsActualHidden =
            locked && hideActualLegend && hideExpectedLegend;

        return { allExpectedVsActualHidden };
    }

    function getPercentageOfMarketVolumeLegendStatus() {
        const {
            PercentageOfMarketVolume: {
                hidePercentageOfVolumeLegend,
                hideAvgPercentageOfVolumeLegend,
            },
        } = config;
        const allPercentageOfMarketVolumeHidden =
            locked &&
            hidePercentageOfVolumeLegend &&
            hideAvgPercentageOfVolumeLegend;
        return { allPercentageOfMarketVolumeHidden };
    }

    function getPlChartLegendStatus() {
        const {
            PnL: { hideRealizedPnLLegend, hideTotalPnLLegend },
        } = config;
        const allPlChartHidden =
            locked && hideRealizedPnLLegend && hideTotalPnLLegend;
        return { allPlChartHidden };
    }

    function getPerformanceVsIndexLegendStatus() {
        const {
            PerformanceVsIndex: {
                hideIndexLegend,
                hidePerformanceVsIndexLegend,
            },
        } = config;
        const allPerformanceVsIndexChartHidden =
            locked && hideIndexLegend && hidePerformanceVsIndexLegend;
        return { allPerformanceVsIndexChartHidden };
    }
}
